import React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface TimePickerProps {
  date: Date | undefined;
  onChange: (date: Date) => void;
  className?: string;
}

/**
 * TimePicker - 24-hour time input component.
 * Allows selecting hours (0-23) and minutes (0-59) with no AM/PM control.
 */
export const TimePicker: React.FC<TimePickerProps> = ({
  date,
  onChange,
  className,
}) => {
  const hours = date ? date.getHours() : 0;
  const minutes = date ? date.getMinutes() : 0;

  // Local state for input values to allow free typing
  const [hoursInput, setHoursInput] = React.useState(
    hours.toString().padStart(2, '0')
  );
  const [minutesInput, setMinutesInput] = React.useState(
    minutes.toString().padStart(2, '0')
  );

  // Update local state when external date changes
  React.useEffect(() => {
    setHoursInput(hours.toString().padStart(2, '0'));
  }, [hours]);

  React.useEffect(() => {
    setMinutesInput(minutes.toString().padStart(2, '0'));
  }, [minutes]);

  const updateHours = (value: string): void => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0 && num <= 23) {
      const newDate = date ? new Date(date) : new Date();
      newDate.setHours(num);
      onChange(newDate);
    }
  };

  const updateMinutes = (value: string): void => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0 && num <= 59) {
      const newDate = date ? new Date(date) : new Date();
      newDate.setMinutes(num);
      onChange(newDate);
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    // Allow empty or numeric input (max 2 digits)
    if (value === '' || /^\d{0,2}$/.test(value)) {
      setHoursInput(value);
    }
  };

  const handleHoursBlur = (): void => {
    if (hoursInput === '') {
      setHoursInput('00');
      updateHours('0');
    } else {
      const num = parseInt(hoursInput, 10);
      if (!isNaN(num)) {
        // Clamp to valid range
        const clamped = Math.max(0, Math.min(23, num));
        const formatted = clamped.toString().padStart(2, '0');
        setHoursInput(formatted);
        updateHours(clamped.toString());
      } else {
        setHoursInput(hours.toString().padStart(2, '0'));
      }
    }
  };

  const handleMinutesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    // Allow empty or numeric input (max 2 digits)
    if (value === '' || /^\d{0,2}$/.test(value)) {
      setMinutesInput(value);
    }
  };

  const handleMinutesBlur = (): void => {
    if (minutesInput === '') {
      setMinutesInput('00');
      updateMinutes('0');
    } else {
      const num = parseInt(minutesInput, 10);
      if (!isNaN(num)) {
        // Clamp to valid range
        const clamped = Math.max(0, Math.min(59, num));
        const formatted = clamped.toString().padStart(2, '0');
        setMinutesInput(formatted);
        updateMinutes(clamped.toString());
      } else {
        setMinutesInput(minutes.toString().padStart(2, '0'));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger blur to validate and format
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label className="text-xs font-medium text-muted-foreground">Time</label>
      <div className="flex items-center gap-2 justify-center">
        <Input
          type="text"
          inputMode="numeric"
          value={hoursInput}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          onKeyDown={handleKeyDown}
          className="w-16 text-center"
          placeholder="HH"
        />
        <span className="text-muted-foreground font-medium">:</span>
        <Input
          type="text"
          inputMode="numeric"
          value={minutesInput}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          onKeyDown={handleKeyDown}
          className="w-16 text-center"
          placeholder="MM"
        />
      </div>
    </div>
  );
};
